import React, { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import AccountSettingsNav from '@/components/AccountSettingsNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { UserPlus, UserMinus, Users, MailPlus, Loader2, Building2 } from 'lucide-react'
import { initialsFromEmail } from '@/lib/identity'
import { formatLongDate } from '@/lib/account-dates'
import { cn } from '@/lib/utils'
import HotelAccessPicker, { draftFromAccess, draftIsEmpty, propertyIdsFor } from '@/components/account/HotelAccessPicker'
import HotelAccessDialog from '@/components/account/HotelAccessDialog'
import type { AccountSettingsSharedProps } from '@/types/shared'
import type { HotelAccess, HotelAccessDraft, HotelPicker, TeamMember } from '@/types/team'

interface AssignableRole {
  value: string
  label: string
  description: string
}

interface TeamProps extends AccountSettingsSharedProps {
  members: TeamMember[]
  assignableRoles: AssignableRole[]
  hotelPicker: HotelPicker
}

const ALL_HOTELS_DRAFT: HotelAccessDraft = { scope: 'all', selected: [] }

// "All hotels" reads as the quiet default; a chosen list reads in the primary family, so a limited
// teammate stands out when scanning the table (CLAUDE.md badge sizes, §5.17.3 colours).
function HotelAccessBadge({ access }: { access: HotelAccess }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'whitespace-nowrap border-transparent text-[11px] font-medium',
        access.scope === 'all' ? 'bg-slate-100 text-slate-700' : 'bg-teal-50 text-teal-700'
      )}
    >
      {access.label}
    </Badge>
  )
}

export default function Team({
  currentUser,
  currentAccount,
  accountAbilities,
  activeSection,
  members,
  assignableRoles,
  hotelPicker,
}: TeamProps) {
  const canManage = accountAbilities.manageMembers

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('manager')
  const [inviteHotels, setInviteHotels] = useState<HotelAccessDraft>(ALL_HOTELS_DRAFT)
  const [inviting, setInviting] = useState(false)

  // "Change hotels": the teammate whose dialog is open (one at a time).
  const [changingHotels, setChangingHotels] = useState<TeamMember | null>(null)

  // Confirm dialogs — one member (or self, for leaving) pending at a time.
  const [removing, setRemoving] = useState<TeamMember | null>(null)
  const [leaving, setLeaving] = useState(false)

  // The pending member whose invitation is being sent again (one at a time).
  const [resendingId, setResendingId] = useState<number | null>(null)

  const ownerCount = members.filter((m) => m.role === 'owner').length
  const inviteRoleDescription = assignableRoles.find((r) => r.value === inviteRole)?.description
  // Owners and admins always open every hotel, so the hotel choice appears only for these roles.
  const inviteRoleIsScopable = hotelPicker.scopableRoles.includes(inviteRole)
  const inviteBlocked = inviteRoleIsScopable && draftIsEmpty(inviteHotels)

  // An email that is already on the team opens the choice on the hotels that person has NOW, so
  // re-inviting them (to change a role, say) never widens them to every hotel by default.
  const typedEmail = inviteEmail.trim().toLowerCase()
  const existingMember = typedEmail ? members.find((m) => m.email.toLowerCase() === typedEmail) ?? null : null
  const existingMemberId = existingMember?.membershipId ?? null
  useEffect(() => {
    setInviteHotels(existingMember ? draftFromAccess(existingMember.hotelAccess, hotelPicker.options) : ALL_HOTELS_DRAFT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingMemberId])

  function sendInvite() {
    const email = inviteEmail.trim()
    if (!email || inviteBlocked) return
    setInviting(true)
    // The hotel choice travels only with a role it applies to; without it the server leaves a
    // teammate's hotels exactly as they are.
    const hotels = inviteRoleIsScopable
      ? { hotel_access: inviteHotels.scope, property_ids: propertyIdsFor(inviteHotels, hotelPicker.options) ?? [] }
      : {}
    router.post(
      `${currentAccount.settingsPath}/team`,
      { email, role: inviteRole, ...hotels },
      {
        preserveScroll: true,
        onSuccess: () => {
          setInviteEmail('')
          setInviteHotels(ALL_HOTELS_DRAFT)
        },
        onFinish: () => setInviting(false),
      }
    )
  }

  function changeRole(member: TeamMember, role: string) {
    if (role === member.role) return
    router.patch(`${currentAccount.settingsPath}/team/${member.membershipId}`, { role }, { preserveScroll: true })
  }

  // The server decides what goes out (a teammate gets a fresh link and a fresh 14-day window, a claim
  // never moves its window) and answers with the same notice style as inviting.
  function resendInvite(member: TeamMember) {
    setResendingId(member.membershipId)
    router.post(
      `${currentAccount.settingsPath}/team/${member.membershipId}/resend`,
      {},
      { preserveScroll: true, onFinish: () => setResendingId(null) }
    )
  }

  function removeMember(member: TeamMember) {
    router.delete(`${currentAccount.settingsPath}/team/${member.membershipId}`, {
      preserveScroll: true,
      onFinish: () => setRemoving(null),
    })
  }

  function leaveBusiness(member: TeamMember) {
    router.delete(`${currentAccount.settingsPath}/team/${member.membershipId}`, {
      onFinish: () => setLeaving(false),
    })
  }

  const self = members.find((m) => m.isSelf)
  // The last remaining owner can't leave — someone must own the business.
  const canLeave = !!self && !(self.role === 'owner' && ownerCount === 1)

  return (
    <PortfolioLayout title="Team" currentUser={currentUser}>
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Heading */}
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Business settings</h1>
            <HelpTooltip content="Who has access to this business and what they can do. Owners and admins can invite teammates and change roles." />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">The people who work in {currentAccount.name} and their access levels.</p>
        </section>

        <AccountSettingsNav accountAbilities={accountAbilities} activeSection={activeSection} settingsPath={currentAccount.settingsPath} />

        {/* Members */}
        <section className="animate-slide-in" style={{ animationDelay: '100ms' }}>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Team members</h2>
            <HelpTooltip content="A member's role decides what they can see and change. Invited teammates show as Invited until they set a password, and their link can be sent again." />
          </div>

          {members.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Users className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No team members yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Invite a teammate below to give them access.</p>
            </div>
          ) : (
            // Four columns, so the table fits the settings column with no sideways scroll once a Hotels
            // column is added: whether someone has joined, and when, reads under their email. On a
            // phone each member is a stacked block (header hidden, the controls wrap under the email),
            // the ReviewRows pattern, so "Change hotels" is never off the side of the screen.
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <Table className="max-sm:block">
                <TableHeader className="max-sm:hidden">
                  <TableRow>
                    <TableHead className="px-3 first:pl-4">Member</TableHead>
                    <TableHead className="px-3">Role</TableHead>
                    <TableHead className="px-3">
                      <span className="inline-flex items-center">
                        Hotels
                        <HelpTooltip content="Managers and viewers open only the hotels chosen for them, while owners and admins always open every hotel." />
                      </span>
                    </TableHead>
                    <TableHead className="px-3 text-right last:pr-4" />
                  </TableRow>
                </TableHeader>
                <TableBody className="max-sm:block">
                  {members.map((member) => (
                    <TableRow
                      key={member.membershipId}
                      className="max-sm:flex max-sm:flex-wrap max-sm:items-center max-sm:gap-x-2 max-sm:gap-y-2 max-sm:px-4 max-sm:py-3"
                    >
                      <TableCell className="px-3 first:pl-4 max-sm:w-full max-sm:p-0">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {initialsFromEmail(member.email)}
                          </span>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              <span className="break-all text-sm font-medium text-foreground">{member.email}</span>
                              {member.isSelf && (
                                <Badge variant="outline" className="border-border bg-muted text-[11px] text-muted-foreground">
                                  You
                                </Badge>
                              )}
                            </div>
                            {/* What the Status and Joined columns said, in one line: an invitation still
                                waiting reads in amber, so a pending teammate stands out when scanning. */}
                            <p
                              className={cn('mt-0.5 text-xs', member.invitationPending ? 'text-amber-700' : 'text-muted-foreground')}
                              data-testid="member-since"
                            >
                              {member.invitationPending ? 'Invited' : 'Joined'} {formatLongDate(member.joinedAt)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="px-3 max-sm:p-0">
                        {canManage && !member.isSelf ? (
                          <Select value={member.role} onValueChange={(role) => changeRole(member, role)}>
                            <SelectTrigger className="h-8 w-32 text-xs" aria-label={`Role for ${member.email}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {assignableRoles.map((role) => (
                                <SelectItem key={role.value} value={role.value}>
                                  {role.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="border-border bg-muted text-[11px] capitalize text-muted-foreground">
                            {member.role}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="px-3 max-sm:p-0">
                        <div className="flex items-center gap-1.5">
                          <HotelAccessBadge access={member.hotelAccess} />
                          {member.hotelAccess.editable && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 cursor-pointer whitespace-nowrap rounded-lg px-2 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => setChangingHotels(member)}
                              aria-label={`Change hotels for ${member.email}`}
                            >
                              <Building2 className="mr-1 h-3.5 w-3.5" />
                              Change hotels
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-3 text-right last:pr-4 max-sm:ml-auto max-sm:p-0">
                        <div className="flex items-center justify-end gap-1">
                          {canManage && !member.isSelf && member.invitationPending && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg px-2 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => resendInvite(member)}
                              disabled={resendingId !== null}
                              aria-label={`Resend invite to ${member.email}`}
                            >
                              {resendingId === member.membershipId ? (
                                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <MailPlus className="mr-1.5 h-3.5 w-3.5" />
                              )}
                              Resend invite
                            </Button>
                          )}
                          {canManage && !member.isSelf && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 rounded-lg p-0 text-rose-500 hover:text-rose-600"
                              onClick={() => setRemoving(member)}
                              aria-label={`Remove ${member.email}`}
                            >
                              <UserMinus className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {member.isSelf && canLeave && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 rounded-lg px-2 text-xs text-rose-500 hover:text-rose-600"
                              onClick={() => setLeaving(true)}
                            >
                              Leave business
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        {/* Invite */}
        {canManage && (
          <section className="animate-slide-in" style={{ animationDelay: '150ms' }}>
            <div className="rounded-xl border border-border/60 bg-white shadow-sm">
              <div className="border-b border-border/40 px-5 py-4">
                <h2 className="text-sm font-semibold text-foreground">Invite a teammate</h2>
                <p className="text-xs text-muted-foreground">Invited teammates get an email to set their password.</p>
              </div>
              <div className="p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="invite-email">Email address</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="colleague@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') sendInvite()
                      }}
                    />
                  </div>
                  <div className="w-full space-y-2 sm:w-44">
                    <Label htmlFor="invite-role">Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger id="invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {assignableRoles.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    className="cursor-pointer rounded-lg"
                    onClick={sendInvite}
                    disabled={inviting || !inviteEmail.trim() || inviteBlocked}
                  >
                    <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                    Send invite
                  </Button>
                </div>
                {inviteRoleDescription && <p className="mt-2 text-xs text-muted-foreground">{inviteRoleDescription}</p>}
                {inviteRoleIsScopable && (
                  <div className="mt-4 animate-slide-in space-y-2 border-t border-border/40 pt-4" data-testid="invite-hotel-access">
                    <Label className="text-sm font-semibold text-foreground">Hotels they can open</Label>
                    <HotelAccessPicker
                      picker={hotelPicker}
                      draft={inviteHotels}
                      onChange={setInviteHotels}
                      disabled={inviting}
                      listClassName="max-h-64"
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Role legend */}
        <section className="animate-slide-in" style={{ animationDelay: '200ms' }}>
          <div className="rounded-xl border border-border/60 bg-white shadow-sm">
            <div className="border-b border-border/40 px-5 py-4">
              <h2 className="text-sm font-semibold text-foreground">What each role can do</h2>
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-5 sm:grid-cols-2">
              {assignableRoles.map((role) => (
                <div key={role.value}>
                  <dt className="text-sm font-medium text-foreground">{role.label}</dt>
                  <dd className="mt-0.5 text-xs text-muted-foreground">{role.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </div>

      <HotelAccessDialog
        settingsPath={currentAccount.settingsPath}
        businessName={currentAccount.name}
        picker={hotelPicker}
        member={changingHotels}
        onClose={() => setChangingHotels(null)}
      />

      {/* Remove confirmation */}
      <AlertDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {removing?.email} from {currentAccount.name}? They lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRemoving(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={() => removing && removeMember(removing)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Leave-business confirmation */}
      <AlertDialog open={leaving} onOpenChange={setLeaving}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave this business</AlertDialogTitle>
            <AlertDialogDescription>
              Leave {currentAccount.name}? You lose access immediately and will need a new invite to return.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setLeaving(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={() => self && leaveBusiness(self)}
            >
              Leave business
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PortfolioLayout>
  )
}
